import React from 'react';
import { Clock } from 'lucide-react';
import { useRestaurantStatus } from '../hooks/useRestaurantStatus';

/**
 * Displays real-time restaurant open/closed status.
 * Status is fetched from backend — always reflects manual admin setting.
 * No automatic opening hours logic.
 */
const OpenStatusIndicator = ({ compact = false }) => {
  const { loaded, isOpen, deliveryEnabled, takeawayEnabled, dineinEnabled } = useRestaurantStatus();

  // While the first fetch is in flight, show a neutral "Betöltés..." state
  // instead of flashing the fail-closed "Zárva" badge.
  if (!loaded) {
    if (compact) {
      return (
        <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full font-semibold text-sm bg-gray-100 text-gray-600 border border-gray-300">
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></span>
          <span>Betöltés...</span>
        </div>
      );
    }
    return (
      <div className="rounded-2xl p-4 shadow-lg bg-gray-50 border-2 border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-gray-300">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-black text-gray-600">Betöltés...</h3>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full font-semibold text-sm ${
        isOpen
          ? 'bg-green-100 text-green-800 border border-green-300'
          : 'bg-red-100 text-red-800 border border-red-300'
      }`}>
        <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></span>
        <span>{isOpen ? 'Nyitva' : 'Zárva'}</span>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-4 shadow-lg transition-all duration-300 ${
      isOpen
        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300'
        : 'bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300'
    }`}>
      <div className="flex items-start space-x-3">
        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
          isOpen ? 'bg-green-500' : 'bg-red-500'
        }`}>
          <Clock className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`w-3 h-3 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></span>
            <h3 className={`text-xl font-black ${isOpen ? 'text-green-800' : 'text-red-800'}`}>
              {isOpen ? '🟢 Nyitva' : '🔴 Zárva'}
            </h3>
          </div>

          {isOpen && (
            <div className="mt-2 space-y-1">
              {[
                { label: '🥡 Elvitel', enabled: takeawayEnabled },
                { label: '🛵 Házhozszállítás', enabled: deliveryEnabled },
                { label: '🍽️ Helyben fogyasztás', enabled: dineinEnabled },
              ].map(({ label, enabled }) => (
                <p key={label} className={`text-sm font-semibold ${enabled ? 'text-green-700' : 'text-gray-400 line-through'}`}>
                  {enabled ? '✓' : '✗'} {label}
                </p>
              ))}
            </div>
          )}

          {!isOpen && (
            <p className="text-red-700 font-semibold text-sm mt-1">
              Online rendelés jelenleg nem elérhető.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenStatusIndicator;
